<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl" class="mb-3">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("customerAdminAccountList") }}</h4>
        <div>
          <b-button v-if="!decreeChoose" variant="outline-success" class="ml-4" :disabled="!totalItems" @click="debounceOnClickExportToExcel">
            <b-icon-download class="mr-2"></b-icon-download> {{ $t("export") }}
          </b-button>
        </div>
      </div>

      <b-form class="d-flex flex-row align-items-center mb-3"
              @submit.prevent="onSubmitSearch">
        <div v-if="!isManager()" class="flex-shrink-0 mr-3">{{ $t("saleArea2") }}:</div>
        <b-form-select v-if="!isManager()" class="mr-5" :options="areaFilterOptions" v-model="areaFilterSelectedOption"/>
        <div class="flex-shrink-0 mr-3">{{ $t("search") }}:</div>
        <b-form-input type="search" v-model="searchText" minlength="2" maxlength="50" :placeholder="$t('adminSearchPlaceholder')" class="mr-5" />
        <b-button type="submit" class="flex-shrink-0" variant="primary">{{ $t("search") }}</b-button>
      </b-form>

      <b-table id="tblKhachHang" ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(name)="data">
          {{ data.item.name }}
          <br>
          <span class="fs-14">{{ data.item.email }}</span>
          <br>
          <span class="fs-14">{{ data.item.phone }}</span>
        </template>
        <template v-slot:cell(customerName)="data">
          <router-link :to="`/customers/details/${data.item.customerId}`">{{ data.item.customerName }}</router-link>
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button @click="onClickEditCustomerUser(data.item)">{{ $t("updateInfo") }}</b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickResetPassword(data.item)">{{ $t("resetPassword") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>

      <div class="d-flex flex-row align-items-center justify-content-between">
        <div></div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>
    </b-container>

    <b-modal id="edit-customer-user-modal" :title="$t('updateAccountInfo')" hide-footer no-close-on-backdrop>
      <edit-customer-user-modal :editing-user="userToEdit" @submitted="refreshData" />
    </b-modal>

    <b-modal id="reset-password-modal" :title="$t('resetPassword')" hide-footer no-close-on-backdrop>
      <reset-password-modal :target-user="userToEdit" @submitted="refreshData" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
import {Component, Ref, Vue} from 'vue-property-decorator';
import _ from "lodash";
import BaseComponent from "@/components/BaseComponent";
import {BvTableFieldArray} from "bootstrap-vue";
import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";

import * as util from "@/service/util";
import EditCustomerUserModal from "@/components/customer/user-accounts/EditCustomerUserModal.vue";
import ResetPasswordModal from "@/components/account/ResetPasswordModal.vue";
import * as userService from "@/service/userService";
import * as areaService from "@/service/areaService";
import * as XLSX from "xlsx";
import * as customerService from "@/service/customerService";
import { BounceTimes } from "@/views/enums/TimesNumbers";

import * as exportService from '@/service/exportService';
import { SearchRequestManager } from "@/utils/search-request-manager";

@Component({
  components: { EditCustomerUserModal, ResetPasswordModal }
})
export default class CustomerAdminAccounts extends BaseComponent {
  searchManager = new SearchRequestManager();

  areaFilterSelectedOption: number = null;
  areaFilterOptions = [];
  searchText = "";

  @Ref("table") table: BTable;
  tableFields: BvTableFieldArray = [
    {
      key: "id",
      label: "#",
      thClass: "text-nowrap",
    },
    {
      key: "name",
      label: this.$t("fullName").toString(),
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "username",
      label: this.$t("username").toString(),
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    // {
    //   key: "phone",
    //   label: this.$t("phoneNumber").toString(),
    //   thClass: "text-nowrap",
    // },
    // {
    //   key: "email",
    //   label: "Email",
    //   thClass: "text-nowrap",
    //   tdClass: "text-break",
    // },
    {
      key: "role",
      label: this.$t("role").toString(),
      formatter: value => ({ Customer: this.$t("roleAdmin").toString(), User: this.$t("roleUser").toString() }[value] || value)
    },
    {
      key: "area",
      label: this.$t("saleArea").toString(),
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "customerName",
      label: this.$t("customer").toString(),
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "createdAt",
      label: this.$t("createdDate").toString()
    },
    {
      key: "actions",
      label: "",
      thClass: "text-nowrap",
      tdClass: "text-nowrap"
    }
  ];

  itemsPerPageSelections = [
    { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
    { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
    { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    { value: 200, text: "200 " + this.$t("itemsPerPage").toString() },
  ];
  itemsPerPage = 20;
  currentPage = 1;
  totalItems = 0;

  userToEdit = null;
  decreeChoose = 0;
  
  filterText = "";
  mounted() {
    this.fetchAreas();
  }

  async fetchAreas() {
    try {
      const areas = await areaService.getAll();
      this.areaFilterOptions = areas.map(it => ({ value: it.id, text: it.name }));
      this.areaFilterOptions.unshift({ value: null, text: this.$t("all").toString() });

    } catch (e) {
      this.toastError(e);
    }
  }


  onClickReloadBtn() {
    this.searchManager.reset();
    this.searchText = "";
    this.areaFilterSelectedOption = null;
    this.currentPage = 1;

    this.table.refresh();
  }
  //avoid spam click
  debouncenFilterFormSubmit = _.debounce(() => {
    this.onFilterFormSubmit();
  }, BounceTimes.BOUNCETIMES);

  onFilterFormSubmit() {
    this.table.refresh();
  }

  onSubmitSearch() {
    this.searchManager.onSubmit({
      debounceFn: this.debouncenFilterFormSubmit,
      resetPage: () => {
        this.currentPage = 1;
      },
      execute: () => {
        this.table.refresh();
      },
    });
  }

  refreshData() {
    this.table.refresh();
  }



  onClickEditCustomerUser(user) {
    this.userToEdit = user;
    this.$bvModal.show("edit-customer-user-modal");

  }

  onClickResetPassword(user) {
    this.userToEdit = user;
    this.$bvModal.show("reset-password-modal");
  }


  async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
    const currentRequestId = this.searchManager.nextId();
    try {
      const res = await userService.getCustomerAdminAccounts({
        page: ctx.currentPage,
        pageSize: this.itemsPerPage,
        q: this.searchText?.trim(),
        areaId: this.areaFilterSelectedOption
      });

      if (!this.searchManager.isLatest(currentRequestId)) {
        return [];
      }

      this.totalItems = res.total;
      return res.data;
    } catch (e) {
      if (this.searchManager.isLatest(currentRequestId)) {
        this.toastError(e);
      }
      return [];
    }
  }
debounceOnClickExportToExcel = _.debounce(() => {
    this.onClickExportToExcel ();
  }, BounceTimes.BOUNCETIMES);
  async onClickExportToExcel(): Promise<any> {
      try {
        const customerListRes = await customerService.getCustomers({
          q: this.filterText?.trim(),
          areaId: this.areaFilterSelectedOption,
        }, true, "Users");
        //tên file
        const fileName = `DSTaiKhoanQuanTriKhachHang`;
        // Xuất xlsx
        await exportService.csvExportExcel(customerListRes, fileName);
      } catch (e) {
        // this.toastError(e);
        return [];
      }
  }
}
</script>
